import React, { useCallback, useEffect, useState } from 'react';
import { LayoutTemplate, Save, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getTemplates, saveTemplate, deleteTemplate } from '../../lib/api';

export function NewsletterTemplates({ getContent, onUse }) {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => getTemplates().then(setItems).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const use = (t) => {
    const { subject, hasBody } = getContent();
    if ((subject || hasBody) && !window.confirm(`Replace what's in the editor with the "${t.name}" template?`)) return;
    onUse(t.subject, t.html);
    toast.success(`Loaded "${t.name}"`);
  };
  const save = async (e) => {
    e.preventDefault();
    const { subject, html, hasBody } = getContent();
    if (!name.trim()) { toast.error('Please name the template'); return; }
    if (!hasBody) { toast.error('Write the newsletter first, then save it as a template'); return; }
    const exists = items.find((t) => t.name.toLowerCase() === name.trim().toLowerCase());
    if (exists && !window.confirm(`Update the existing "${exists.name}" template?`)) return;
    setBusy(true);
    try {
      const r = await saveTemplate({ name: name.trim(), subject, html });
      toast.success(r.updated ? `Template "${r.name}" updated` : `Template "${r.name}" saved`);
      setName(''); setSaving(false); load();
    } catch { toast.error('Could not save template'); }
    setBusy(false);
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete the "${t.name}" template?`)) return;
    try { await deleteTemplate(t.id); toast.success('Template deleted'); load(); } catch { toast.error('Could not delete template'); }
  };

  return (
    <div className="bg-[#f8f6f2] border border-neutral-200 rounded-sm p-4 mb-5" data-testid="newsletter-templates">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1.5 font-sans-ah text-[12px] tracking-wide-ah text-[#555] mr-1"><LayoutTemplate size={15} className="text-[#4a9cd4]" /> TEMPLATES</span>
        {items.length === 0 && <span className="font-sans-ah text-[13px] text-[#999]">No templates yet.</span>}
        {items.map((t) => (
          <span key={t.id} className="inline-flex items-center bg-white border border-neutral-300 rounded-full overflow-hidden" data-testid={`template-${t.id}`}>
            <button type="button" onClick={() => use(t)} data-testid={`template-use-${t.id}`} title="Use this template"
              className="font-sans-ah text-[12px] pl-3.5 pr-2 py-1.5 text-[#2f2f2f] hover:text-[#4a9cd4] transition-colors">{t.name}</button>
            <button type="button" onClick={() => remove(t)} aria-label={`Delete ${t.name}`} data-testid={`template-delete-${t.id}`}
              className="pr-2.5 pl-1 py-1.5 text-[#bbb] hover:text-red-500 transition-colors"><X size={13} /></button>
          </span>
        ))}
        {!saving && (
          <button type="button" onClick={() => setSaving(true)} data-testid="template-save-open-btn"
            className="ml-auto font-sans-ah text-[12px] tracking-wide-ah px-3.5 py-2 rounded-sm border border-neutral-300 text-[#555] hover:bg-white flex items-center gap-1.5">
            <Save size={14} /> SAVE AS TEMPLATE
          </button>
        )}
      </div>
      {saving && (
        <form onSubmit={save} className="flex flex-wrap items-center gap-2 mt-3" data-testid="template-save-form">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Template name, e.g. Monthly Update"
            aria-label="Template name" data-testid="template-name-input"
            className="flex-1 min-w-[220px] px-3 py-2 bg-white border border-neutral-300 rounded-sm font-sans-ah text-[13px] focus:outline-none focus:border-[#4a9cd4]" />
          <button type="submit" disabled={busy} data-testid="template-save-btn" className="btn-ah-solid font-sans-ah text-[12px] px-4 py-2 rounded-sm flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} SAVE
          </button>
          <button type="button" onClick={() => { setSaving(false); setName(''); }} data-testid="template-save-cancel-btn" className="font-sans-ah text-[12px] px-3 py-2 text-[#888] hover:text-[#2f2f2f]">Cancel</button>
          <p className="w-full font-sans-ah text-[12px] text-[#999]">Saves the current subject and message. Using an existing name updates that template.</p>
        </form>
      )}
    </div>
  );
}
